import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { UserIdService } from '../../auth';
import { SearchAction } from '../../features/store/actions/search.actions';
import { Country, LoaderError, Pagination, UserInputCheck } from '../../model';
import { Address, AddressSearchParams, DeliveryAddress } from '../../model/address.model';
import { AddressConnector } from '../connectors/address-connector';
import { AddressFacade } from '../facades';
import { UserActions } from '../store/actions';
import {
  DeliveryAddressSelectors,
  OnboardingCountriesSelectors,
  ProfileCountriesSelectors,
} from '../store/selectors/user.selectors';
import { DELIVERY_ADDRESS, StateWithUser } from '../store/user-state';

@Injectable()
export class AddressService implements AddressFacade {
  constructor(private store: Store<StateWithUser>, private userIdService: UserIdService, private connector: AddressConnector) {}

  validatePostalCode(postalCode: string, countryIsoCode?: string): Observable<UserInputCheck> {
    return this.userIdService.takeUserId().pipe(
      switchMap((userId) => {
        return this.connector.validatePostalCode(postalCode, userId, countryIsoCode);
      })
    );
  }

  validatePhoneNumber(phoneNumber: string): Observable<UserInputCheck> {
    return this.userIdService.takeUserId().pipe(
      switchMap((userId) => {
        return this.connector.validatePhoneNumber(userId, phoneNumber);
      })
    );
  }

  /**
   * Returns all delivery countries
   */
  getDeliveryCountries(): Observable<Country[]> {
    return this.store.select(DeliveryAddressSelectors.getDeliveryCountries).pipe(
      tap((countries) => {
        if (!countries) {
          this.loadDeliveryCountries();
        }
      }),
      filter((countries) => !!countries),
      take(1)
    );
  }

  getProfileCountries(): Observable<Country[]> {
    return this.store.select(ProfileCountriesSelectors.getProfileCountries).pipe(
      tap((countries) => {
        if (!countries) {
          this.loadProfileCountries();
        }
      }),
      filter((countries) => !!countries),
      take(1)
    );
  }

  getOnboardingCountries(): Observable<Country[]> {
    return this.store.select(OnboardingCountriesSelectors.getOnboardingCountries).pipe(
      tap((countries) => {
        if (!countries) {
          this.loadOnboardingCountries();
        }
      }),
      filter((countries) => !!countries),
      take(1)
    );
  }

  search(searchParams: AddressSearchParams) {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadDeliveryAddresses(searchParams, userId)),
      () => {}
    );
  }

  getSearchParameters(): Observable<AddressSearchParams> {
    return this.store.select(DeliveryAddressSelectors.getSearchParamsSelector).pipe(
      map((params) => params as AddressSearchParams),
      tap((params: AddressSearchParams) => {
        if (!params || !params.key) {
          this.updateSearchParameters({
            page: 0,
            count: 11,
          });
        }
      }),
      filter((params) => params && !!params.key)
    );
  }

  updateSearchParameters(searchParams: Omit<AddressSearchParams, 'key'>) {
    const key = searchParams.text ? `t=${searchParams.text}` : 'latest';
    this.store.dispatch(new SearchAction({ key, ...searchParams }, DELIVERY_ADDRESS));
  }

  getAddressPagination(): Observable<Pagination | undefined> {
    return this.store.select(DeliveryAddressSelectors.getSearchPaginationSelector);
  }

  getDeliveryAddressByTempId(tempId: string): Observable<Address | undefined> {
    return this.store
      .select(DeliveryAddressSelectors.getSearchResultEntries, { keys: [tempId] })
      .pipe(map((addresses) => addresses?.[0] || undefined));
  }

  getDeliveryAddresses(): Observable<Array<DeliveryAddress>> {
    return this.store.select(DeliveryAddressSelectors.getSearchPageResultsSelector).pipe(
      filter((loaderState) => loaderState.success),
      map((loaderState) => loaderState.value.results),
      switchMap((results) => this.store.select(DeliveryAddressSelectors.getSearchResultEntries, { keys: results }))
    );
  }

  getDeliveryAddressesLoading(): Observable<boolean> {
    return this.store
      .select(DeliveryAddressSelectors.getSearchPageResultsSelector)
      .pipe(map((loaderState) => loaderState.loading));
  }

  getDeliveryAddressesLoadedErrorDetails(): Observable<LoaderError> {
    return this.store
      .select(DeliveryAddressSelectors.getSearchPageResultsSelector)
      .pipe(map((loaderState) => loaderState.errorDetails));
  }

  deleteDeliveryAddress(id: string) {
    this.getSearchParameters()
      .pipe(take(1))
      .subscribe((params) => {
        return this.userIdService.takeUserId(true).subscribe(
          (userId) => this.store.dispatch(new UserActions.DeleteDeliveryAddress(id, userId, params)),
          () => {}
        );
      });
  }

  deleteDeliveryAddressSuccess(): Observable<boolean> {
    return this.store.select(DeliveryAddressSelectors.getDeliveryAddressDeleteSuccess);
  }

  deleteDeliveryAddressReset(): void {
    this.store.dispatch(new UserActions.DeleteDeliveryAddressReset());
  }

  saveDeliveryAddress(address: Address, generateTempCode: boolean = false): Observable<string | undefined> {
    const tempCode = generateTempCode ? this.generateTempCode() : undefined;
    return combineLatest([this.userIdService.getUserId(), this.getSearchParameters()]).pipe(
      take(1),
      map(([userId, params]) => {
        this.store.dispatch(new UserActions.SaveDeliveryAddress(address, userId, params, tempCode));
        return tempCode;
      })
    );
  }

  saveDeliveryAddressReset() {
    this.store.dispatch(new UserActions.SaveDeliveryAddressReset());
  }

  saveDeliveryAddressLoading(): Observable<boolean> {
    return this.store.select(DeliveryAddressSelectors.getDeliveryAddressLoading);
  }

  saveDeliveryAddressFailure(): Observable<LoaderError | undefined> {
    return this.store.select(DeliveryAddressSelectors.getDeliveryAddressFailure);
  }

  saveDeliveryAddressSuccess(): Observable<boolean> {
    return this.store.select(DeliveryAddressSelectors.getDeliveryAddressSuccess);
  }

  private loadDeliveryCountries(): void {
    this.userIdService.takeUserId().subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadDeliveryCountries(userId)),
      () => {}
    );
  }

  private loadProfileCountries(): void {
    this.userIdService.takeUserId().subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadProfileCountries(userId)),
      () => {}
    );
  }

  private loadOnboardingCountries(): void {
    this.userIdService.takeUserId().subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadOnboardingCountries(userId)),
      () => {}
    );
  }

  private generateTempCode(): string {
    const pseudoUuid = Math.random().toString(36).substr(2, 9);
    return `deliveryAddress-${pseudoUuid}`;
  }
}
